import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { exportLeadsCsv } from "../lib/exportCsv";
import { US_STATES } from "../lib/constants";
import { UserAccessPanel } from "../components/UserAccessPanel";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatsBar } from "../components/StatsBar";
import { LeadList } from "../components/LeadList";
import { LeadMap } from "../components/LeadMap";
import { LeadDrawer } from "../components/LeadDrawer";
import { ScanDialog } from "../components/ScanDialog";
import { ByState, ByCategory, ByPos, ScanHistory } from "../components/SidePanels";
import { DateBackfillPanel } from "../components/DateBackfillPanel";
import { ReasonFilter } from "../components/ArchiveDialog";
import { RepEmailDialog } from "../components/RepEmailDialog";
import { OwnerPitchDialog } from "../components/OwnerPitchDialog";
import { AlertStatesPicker } from "../components/AlertStatesPicker";
import { BigPanel } from "../components/BigPanel";
import { PotentialOwnerPicker } from "../components/PotentialOwnerPicker";
import { DeepDiveBar } from "../components/DeepDiveBar";
import { RepsBoard } from "../components/RepsBoard";
import { PdfBar } from "../components/PdfBar";
import { AddLeadButton } from "../components/AddLeadButton";
import { DealStageFilter } from "../components/DealStage";
import { ManualButton } from "../components/ManualViewer";
import { DashboardHeader, OwnerEmailChip, SentChip, NotEmailedChip, OwnerGotEmailChip, PageTitle, DemoBanner } from "../components/DashboardParts";
import { useLeadsData, useScans, useTeam, useSelection } from "../hooks/useDashboard";

const loadSavedView = (user) => {
  try {
    const v = JSON.parse(localStorage.getItem(`pf-view-${user?.id}`) || "{}");
    const f = Object.fromEntries(Object.entries(v.filters || {}).filter(([k, val]) => k in INITIAL && typeof val === "string"));
    if (["active", "any"].includes(f.bucket) && user?.role !== "admin") f.bucket = "all";
    if (user?.role === "demo" && f.bucket !== "other") f.bucket = "all";
    return { filters: f, tab: ["leads", "big", "team"].includes(v.tab) && (v.tab !== "team" || user?.role === "admin") ? v.tab : "leads" };
  } catch { return { filters: {}, tab: "leads" }; }
};

const INITIAL = { q: "", category: "all", state: "all", platform: "all", pos: "all", assigned: "all", followup: "all", age: "all", bucket: "all", archive_reason: "all", owner_email: "all", sent: "all", not_emailed: "all", owner_got_email: "all", rep_emailed: "", rep_potential: "", potential_of: "", viewing: "", deal_stage: "", sort: "newest" };

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [saved] = useState(() => loadSavedView(user));
  const [filters, setFilters] = useState(() => {
    const bucket = ["all", "future", "potential", "archived", "other", "signed"].includes(saved.filters.bucket) ? saved.filters.bucket : "all";
    return { ...INITIAL, bucket, potential_of: bucket === "potential" && user?.role === "admin" ? "all" : "" };
  });
  const [active, setActive] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [tab, setTab] = useState(saved.tab);
  useEffect(() => { if (user?.id) localStorage.setItem(`pf-view-${user.id}`, JSON.stringify({ filters, tab })); }, [user?.id, filters, tab]);
  const [repLead, setRepLead] = useState(null);
  const [pitchLead, setPitchLead] = useState(null);
  const emailRep = user?.role !== "demo" ? setRepLead : undefined;
  const { leads: rawLeads, setLeads, stats, loading, loadLeads } = useLeadsData(filters);
  const leads = rawLeads;
  const { scans, setScans, running } = useScans(loadLeads);
  const { team, teamById, loadTeam } = useTeam();
  const sel = useSelection(leads);
  const isDemo = user?.role === "demo";
  const isLimited = !!user?.limited;
  useEffect(() => {
    if (isLimited && !["potential", "signed"].includes(filters.bucket)) setFilters((f) => ({ ...f, bucket: "potential", potential_of: "", viewing: "" }));
    if (isLimited && tab === "big") setTab("leads");
  }, [isLimited, filters.bucket, tab]); // eslint-disable-line react-hooks/exhaustive-deps
  const isAdmin = user?.role === "admin";
  const isViewer = !!user?.reps_viewer;
  const viewingOthers = !isAdmin && !!filters.potential_of && ["potential", "signed"].includes(filters.bucket);
  const othersDeal = (l) => !isAdmin && !!l && (l.view_only || (["potential", "signed"].includes(l.bucket) && l[`${l.bucket}_uid`] !== user?.id));
  const allLabel = user?.reps_viewer ? "All leads" : "Your Leads";
  const myMember = !isAdmin && team.find((m) => m.name.trim().toLowerCase() === (user?.name || "").trim().toLowerCase());
  const setFilter = (key) => (v) => setFilters((f) => ({ ...f, [key]: v }));

  const updateLead = async (lead, patch, noteDelta, countKey = "notes_count") => {
    if (noteDelta) {
      const next = { ...lead, [countKey]: (lead[countKey] || 0) + noteDelta };
      setActive((a) => (a && a.id === next.id ? next : a));
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? next : l)));
      return;
    }
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, patch);
      if (patch.outcome === "not_now" && data.follow_up_date) toast.success(`Not now — follow-up set for ${data.follow_up_date}`);
      if (data.alert_sent === true) toast.success(`Assigned to ${teamById[patch.assigned_to]?.name} — they've been emailed`);
      else if (data.alert_sent === false) toast.warning(`Assigned to ${teamById[patch.assigned_to]?.name}, but the email alert couldn't be sent`);
      setActive((a) => (a && a.id === data.id ? data : a));
      setLeads((ls) => ls.map((l) => (l.id === data.id ? data : l)));
      loadLeads();
      return data;
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const SECTION_NAMES = { future: "Future Leads", other: "Around Me", potential: "Potential", signed: "Signed", archived: "Dead deals" };
  // Where to Next? — m = { bucket, reason, note, for_uid, forName }
  const moveOne = async (lead, m) => {
    try {
      const { data } = await api.post("/leads/bucket", { lead_ids: [lead.id], bucket: m.bucket, reason: m.reason, note: m.note, for_uid: m.for_uid || null, jeff_note: m.jeff_note || null });
      const where = m.bucket ? `${m.forName ? `${m.forName}'s ` : ["potential", "other"].includes(m.bucket) ? "your " : ""}${SECTION_NAMES[m.bucket]}` : allLabel;
      if (!data.moved) toast.message(`${lead.name} is already there`);
      else toast.success(`${lead.name} moved to ${where}${m.bucket === "signed" ? (data.rep_emailed ? ` · ${m.forName} has been emailed` : ` · ${m.forName} has no email on file`) : ""}`);
      setActive(null);
      loadLeads();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const stageChanged = (l) => {
    setActive((a) => (a && a.id === l.id ? l : a));
    setLeads((ls) => ls.map((x) => (x.id === l.id ? l : x)));
  };

  const markOwner = async (lead, marked) => {
    try {
      const { data } = await api.post(`/leads/${lead.id}/owner-emailed`, { marked });
      setActive(data);
      toast.success(marked ? "Marked as Sent Owner Email" : "Mark removed");
      loadLeads();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCsv = () => {
    const label = Object.entries(filters)
      .filter(([k, v]) => v && v !== "all" && k !== "q")
      .map(([k, v]) => (k === "assigned" ? teamById[v]?.name || v : v))
      .concat(filters.q ? [`search-${filters.q}`] : [])
      .join("-").replace(/[^a-z0-9-]+/gi, "_") || "all";
    exportLeadsCsv(leads, teamById, label.toLowerCase())
      .then(() => toast.success(`Exported ${leads.length} leads to Excel`)).catch((e) => toast.error(`Export failed: ${e.message}`));
  };

  return (
    <div className="min-h-screen bg-[#f3f5f9]">
      <DashboardHeader tab={tab} setTab={setTab} teamCount={team.length} running={running}
        onScan={() => setScanOpen(true)} user={user} onLogout={logout} />
      {!isDemo && <div className="max-w-[1600px] mx-auto px-3 sm:px-6 pt-3 -mb-5 flex justify-end relative z-10"><ManualButton /></div>}

      {tab === "big" && (
        <main className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8 space-y-6">
          <PageTitle title="BIG" subtitle="Multi-location restaurant groups worth a call. Search any state you cover." />
          <BigPanel team={team} goToTeam={() => setTab("team")} senderName={user?.name} onLeadsChanged={loadLeads} role={user?.role} allowedStates={user?.allowed_states} myId={user?.id} />
        </main>
      )}
      {tab === "team" && isAdmin && (
        <main className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8 space-y-6">
          <PageTitle title="Your team" subtitle="Co-workers you can share leads with. Tick leads on the Leads tab, then hit “Share selected”." />
          {user?.role === "admin" && <UserAccessPanel onTeamChange={loadTeam} />}
        </main>
      )}
      {/* leads view stays mounted (hidden) on the Team tab so the map isn't torn down mid-animation */}
      <main className={`max-w-[1600px] mx-auto px-4 sm:px-6 py-8 space-y-6 pb-28 ${tab === "leads" ? "" : "hidden"}`}>
        <div className="flex items-start justify-between gap-3">
        <PageTitle title={filters.bucket === "other" ? "Around Me" : ({ future: "Future Leads", potential: "Potential", signed: "Signed", archived: "Dead deals" })[filters.bucket] || "PayWick Team App"}
          subtitle={`${filters.bucket === "other" ? "Restaurants with their own website" : "Businesses"} ${filters.bucket === "other" && !user?.deep_by_state ? "within 50 miles of you" : user?.allowed_states?.length ? `in ${user.allowed_states.map((s) => US_STATES[s]).join(", ")}` : "across the US"}${filters.bucket === "other" ? ", found by Deep dive." : filters.bucket === "future" ? " that mention their POS but haven't complained or asked for a new one yet." : " publicly unhappy with their POS or asking for a new one."}`} />
          {isAdmin && <AddLeadButton myId={user?.id} onAdded={loadLeads} />}
        </div>
        {isViewer && <RepsBoard refreshKey={leads} viewOnly={!isAdmin} onOpen={(r, key) => {
          sel.clear();
          setFilters(["potential", "signed"].includes(key) ? { ...INITIAL, bucket: key, potential_of: r.user_id, viewing: r.name } : { ...INITIAL, age: "all", bucket: "any", [key]: r.name });
        }} />}
        {!isAdmin && filters.potential_of && ["potential", "signed"].includes(filters.bucket) && (
          <div className="flex items-center justify-between rounded-xl bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm text-amber-900" data-testid="viewing-rep-banner">
            <span>Viewing {filters.potential_of === "all" ? "all reps'" : `${filters.viewing || "a rep"}'s`} {filters.bucket === "signed" ? "Signed" : "Potential"} deals · view only</span>
            <button onClick={() => setFilters((f) => ({ ...f, potential_of: "", viewing: "" }))} className="font-semibold text-amber-800 hover:underline" data-testid="viewing-rep-back">Back to mine</button>
          </div>
        )}
        {filters.bucket === "any" && (
          <div className="flex items-center justify-between rounded-xl bg-blue-50 border border-blue-100 px-4 py-2.5 text-sm text-blue-900" data-testid="rep-filter-banner">
            <span>Showing {filters.rep_emailed ? `businesses ${filters.rep_emailed} emailed` : `leads ${filters.rep_potential} moved to Potential`} (all views)</span>
            <button onClick={() => setFilters({ ...INITIAL })} className="font-semibold text-blue-700 hover:underline" data-testid="rep-filter-clear">Clear</button>
          </div>
        )}
        <StatsBar limited={isLimited} buckets={stats?.buckets} allLabel={allLabel} viewing={viewingOthers ? filters.viewing : ""} pickedOther={isAdmin && filters.bucket === "other" && !!filters.potential_of} view={filters.bucket} isAdmin={isAdmin} isDemo={isDemo}
          onView={(v) => { sel.clear(); setFilters((f) => ({ ...f, bucket: v, archive_reason: "all", potential_of: ["potential", "signed"].includes(v) && isAdmin ? "all" : "", viewing: "", deal_stage: "" })); }} />
        {filters.bucket === "other" && (
          <DeepDiveBar isDemo={isDemo} allowedStates={user?.allowed_states} byState={user?.deep_by_state} userId={user?.id} scans={scans} onStarted={(s) => setScans((prev) => [s, ...prev])} />
        )}
        {isAdmin && ["potential", "other", "signed"].includes(filters.bucket) && (
          <div className="flex flex-wrap gap-3">
            <PotentialOwnerPicker label={{ other: "Around Me", signed: "Signed" }[filters.bucket] || "Potential"} value={filters.potential_of} myId={user?.id} onChange={(v) => { sel.clear(); setFilters((f) => ({ ...f, potential_of: v })); }} />
            {filters.bucket === "potential" && <DealStageFilter value={filters.deal_stage} onChange={(v) => { sel.clear(); setFilters((f) => ({ ...f, deal_stage: v })); }} />}
          </div>
        )}
        {isDemo ? <DemoBanner other={filters.bucket === "other"} /> : (
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex rounded-full bg-slate-100 p-1" data-testid="lead-sort">
              {[["newest", "Newest first"], ["oldest", "Oldest first"]].map(([k, l]) => (
                <button key={k} onClick={() => setFilters((f) => ({ ...f, sort: k }))} data-testid={`lead-sort-${k}`}
                  className={`h-8 px-3 rounded-full text-xs font-semibold transition-colors ${(filters.sort || "newest") === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}>{l}</button>
              ))}
            </div>
            <OwnerEmailChip active={filters.owner_email === "yes"} onToggle={() => setFilters((f) => ({ ...f, owner_email: f.owner_email === "yes" ? "all" : "yes" }))} testid="owner-email-filter" />
            <NotEmailedChip active={filters.not_emailed === "yes"} onToggle={() => setFilters((f) => ({ ...f, not_emailed: f.not_emailed === "yes" ? "all" : "yes", owner_got_email: "all" }))} />
            <OwnerGotEmailChip active={filters.owner_got_email === "yes"} onToggle={() => setFilters((f) => ({ ...f, owner_got_email: f.owner_got_email === "yes" ? "all" : "yes", not_emailed: "all" }))} />
            <SentChip isAdmin={isAdmin} active={filters.sent === "yes"} onToggle={() => setFilters((f) => ({ ...f, sent: f.sent === "yes" ? "all" : "yes" }))} />
            {isAdmin && myMember && <span className="text-sm text-slate-600 flex items-center gap-1.5" data-testid="my-alert-states">My alerts: <AlertStatesPicker member={myMember} onSaved={loadTeam} /></span>}
          </div>
        )}
        {filters.bucket === "archived" && (
          <div className="space-y-2">
            <p className="text-sm text-slate-500" data-testid="archived-note">Dead deals: archived leads plus anyone on Toast or Shift4. Open one and use Where to Next? to bring it back.</p>
            <ReasonFilter value={filters.archive_reason} onChange={setFilter("archive_reason")} />
          </div>
        )}
        {user?.allowed_states?.length > 0 && (
          <p className="text-sm text-slate-600 flex items-center gap-2" data-testid="my-area-note">
            <span className="font-mono-label text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-800">Your area</span>
            {user.allowed_states.map((s) => US_STATES[s]).join(", ")}
          </p>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <section className="lg:col-span-7 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500" data-testid="leads-count">{leads.length} lead{leads.length === 1 ? "" : "s"}</p>
              {leads.length > 0 && !isDemo && !viewingOthers && (filters.bucket !== "signed" || isAdmin) && (
                <div className="flex items-center gap-4">
                <button onClick={exportCsv} className="text-sm text-slate-700 font-medium hover:text-blue-700 flex items-center gap-1.5" data-testid="export-csv-button">
                  <Download className="h-4 w-4" />Export to Excel
                </button>
                <button onClick={sel.toggleAllShown} className="text-sm text-blue-700 font-medium hover:underline" data-testid="select-all-shown">
                  {sel.allShown ? "Clear shown" : "Select all shown"}
                </button>
                </div>
              )}
            </div>
            <LeadList leads={leads} groupBy={["potential", "signed"].includes(filters.bucket) && isViewer && filters.potential_of === "all" ? (l) => l.potential_by || "Unknown rep" : undefined} activeId={active?.id} onSelect={setActive} loading={loading} selected={sel.selectedIds} onCheck={sel.toggle} teamById={teamById} selectable={!isDemo && !viewingOthers} onEmailRep={viewingOthers || isLimited ? undefined : emailRep}
              onStage={isAdmin ? stageChanged : undefined}
              emptyText={filters.bucket === "potential" ? "No leads booked yet. Tap Book It on any lead to add it here." : filters.bucket === "signed" ? "No signed deals yet." : undefined} />
          </section>
          <aside className="lg:col-span-5 space-y-4 lg:sticky lg:top-24 self-start">
            <div className="h-[460px]"><LeadMap leads={leads.filter((l) => !l.locked)} active={active} onSelect={setActive} visible={tab === "leads"} /></div>
            <ByState byState={stats?.by_state} current={filters.state} />
            <ByCategory byCategory={stats?.by_category} current={filters.category} />
            <ByPos byPos={stats?.by_pos} current={filters.pos} />
            {!isDemo && <DateBackfillPanel isAdmin={user?.role === "admin"} onFinished={loadLeads} />}
            <ScanHistory scans={scans} />
          </aside>
        </div>
      </main>

      {tab === "leads" && !isDemo && sel.selectedIds.length > 0 && <PdfBar ids={sel.selectedIds} onClear={sel.clear} />}

      <LeadDrawer lead={active} onClose={() => setActive(null)} team={team} onUpdate={updateLead} readOnly={isDemo || isLimited || othersDeal(active)} onMove={moveOne} onEmailRep={othersDeal(active) || isLimited ? undefined : emailRep} isAdmin={isAdmin} onEmailOwner={setPitchLead} onMarkOwner={markOwner} userId={user?.id} userName={user?.name} onStage={isAdmin ? stageChanged : undefined} allLabel={allLabel}
        onSeen={(l) => setLeads((ls) => ls.map((x) => (x.id === l.id ? { ...x, signed_seen: true } : x)))} />
      <OwnerPitchDialog lead={pitchLead} onClose={() => setPitchLead(null)} onSent={(l) => { setActive((x) => (x && x.id === l.id ? l : x)); loadLeads(); }} />
      <RepEmailDialog lead={repLead} allowOther={isAdmin} note={isAdmin ? "It goes into the first picked rep's Potential." : undefined}
        submit={isAdmin && repLead ? (p) => api.post(`/leads/${repLead.id}/email-rep`, p, { params: { assign: true } }).then((r) => r.data) : undefined} onClose={() => setRepLead(null)} team={team} goToTeam={() => setTab("team")} onSent={() => { loadLeads(); if (repLead) api.get(`/leads/${repLead.id}`).then((r) => setActive((x) => (x && x.id === r.data.id ? r.data : x))).catch(() => {}); }} senderName={user?.name} />
      <ScanDialog open={scanOpen} setOpen={setScanOpen} onStarted={(s) => setScans((prev) => [s, ...prev])} allowedStates={user?.allowed_states} />
    </div>
  );
}
